import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Save, Palette, ImageOff } from "lucide-react";
import { toast } from "sonner";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function BrandSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [cfg, setCfg] = useState({
    company_name: "ADA Interceptor",
    tagline: "Silent ADA Compliance Shield",
    accent_hex: "#0033cc",
    logo_url: "",
    footer_line: "Confidential — for the named prospect only",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v1/brand");
        setCfg({
          company_name: r.data.company_name || "",
          tagline: r.data.tagline || "",
          accent_hex: r.data.accent_hex || "#0033cc",
          logo_url: r.data.logo_url || "",
          footer_line: r.data.footer_line || "",
        });
      } catch {
        toast.error("Could not load brand config");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    if (!HEX_RE.test(cfg.accent_hex)) {
      toast.error("Accent color must be a #RRGGBB hex code");
      return;
    }
    if (cfg.logo_url && !/^https?:\/\//.test(cfg.logo_url)) {
      toast.error("Logo URL must start with http(s)://");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/v1/brand", {
        company_name: cfg.company_name || null,
        tagline: cfg.tagline || null,
        accent_hex: cfg.accent_hex,
        logo_url: cfg.logo_url || null,
        footer_line: cfg.footer_line || null,
      });
      setCfg({
        company_name: r.data.company_name || "",
        tagline: r.data.tagline || "",
        accent_hex: r.data.accent_hex || "#0033cc",
        logo_url: r.data.logo_url || "",
        footer_line: r.data.footer_line || "",
      });
      setLogoBroken(false);
      toast.success("Brand identity saved — next PDF download will use these values");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center min-h-[60vh]" data-testid="brand-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div data-testid="brand-settings-page">
      <PageHeader
        eyebrow="Module 06 · Brand & Identity"
        title="White-label PDF dossier"
        description="Re-skin the auto-generated B2B Pitch Dossier PDF with your own agency identity. Cover panel, accent color, optional logo, and footer text are applied to every download and SMTP outreach immediately after save."
        testid="brand-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-[480px_1fr] gap-6">
        <form
          onSubmit={save}
          className="border-2 border-zinc-950 p-5 bg-white"
          data-testid="brand-form"
        >
          <div className="label-xs mb-1">White-label identity</div>

          <Label className="text-xs font-bold uppercase tracking-wider">Company name</Label>
          <Input
            value={cfg.company_name}
            onChange={(e) => setCfg({ ...cfg, company_name: e.target.value })}
            className="rounded-sm h-11 mt-1 font-mono text-sm"
            placeholder="Your Agency, LLC"
            maxLength={80}
            data-testid="brand-company-input"
            required
          />

          <Label className="text-xs font-bold uppercase tracking-wider mt-4 block">Tagline</Label>
          <Input
            value={cfg.tagline}
            onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })}
            className="rounded-sm h-11 mt-1 text-sm"
            placeholder="Silent ADA Compliance Shield"
            maxLength={120}
            data-testid="brand-tagline-input"
          />

          <Label className="text-xs font-bold uppercase tracking-wider mt-4 block">
            Accent color (hex)
          </Label>
          <div className="flex gap-2 mt-1 items-center">
            <Input
              value={cfg.accent_hex}
              onChange={(e) => setCfg({ ...cfg, accent_hex: e.target.value })}
              className="rounded-sm h-11 font-mono text-sm w-44"
              placeholder="#0033cc"
              maxLength={7}
              data-testid="brand-accent-input"
            />
            <input
              type="color"
              value={HEX_RE.test(cfg.accent_hex) ? cfg.accent_hex : "#0033cc"}
              onChange={(e) => setCfg({ ...cfg, accent_hex: e.target.value })}
              className="w-12 h-11 border-2 border-zinc-950 cursor-pointer"
              data-testid="brand-accent-picker"
              aria-label="Pick accent color"
            />
            <div
              className="h-11 flex-1 border-2 border-zinc-950 grid place-items-center font-mono text-xs"
              style={{
                background: HEX_RE.test(cfg.accent_hex) ? cfg.accent_hex : "#0033cc",
                color: "#fff",
              }}
              data-testid="brand-accent-swatch"
            >
              {HEX_RE.test(cfg.accent_hex) ? cfg.accent_hex.toUpperCase() : "invalid"}
            </div>
          </div>

          <Label className="text-xs font-bold uppercase tracking-wider mt-4 block">
            Logo URL (optional · http(s) ·  ≤ 800kb)
          </Label>
          <Input
            value={cfg.logo_url}
            onChange={(e) => {
              setLogoBroken(false);
              setCfg({ ...cfg, logo_url: e.target.value });
            }}
            className="rounded-sm h-11 mt-1 font-mono text-xs"
            placeholder="https://yoursite.com/logo.png"
            data-testid="brand-logo-input"
          />

          <Label className="text-xs font-bold uppercase tracking-wider mt-4 block">
            Footer line
          </Label>
          <Input
            value={cfg.footer_line}
            onChange={(e) => setCfg({ ...cfg, footer_line: e.target.value })}
            className="rounded-sm h-11 mt-1 text-sm"
            placeholder="Confidential — for the named prospect only"
            maxLength={160}
            data-testid="brand-footer-input"
          />

          <Button
            type="submit"
            disabled={saving}
            className="w-full mt-5 h-11 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            data-testid="brand-save-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> Save Brand Identity
              </>
            )}
          </Button>

          <p className="mt-4 text-[11px] leading-snug text-zinc-500">
            All values are applied at PDF compile time. They affect every download from the Pitch
            Lab AND every automated SMTP outreach. The Silent Shield runtime script is not
            re-branded — it stays invisible by design.
          </p>
        </form>

        {/* Live preview of cover panel */}
        <div data-testid="brand-preview">
          <div className="label-xs mb-2 flex items-center gap-2">
            <Palette className="w-3.5 h-3.5" /> Cover Preview · matches PDF output
          </div>
          <div
            className="border-2 border-zinc-950 bg-zinc-950 text-white p-7"
            style={{
              borderBottom: `8px solid ${HEX_RE.test(cfg.accent_hex) ? cfg.accent_hex : "#0033cc"}`,
            }}
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">
              Confidential · B2B Prospect Dossier
            </div>
            <div className="mt-4 flex items-center gap-3">
              {cfg.logo_url && !logoBroken ? (
                <img
                  src={cfg.logo_url}
                  alt="logo"
                  className="h-7 object-contain bg-white/10 px-2 py-1"
                  onError={() => setLogoBroken(true)}
                  data-testid="brand-preview-logo"
                />
              ) : cfg.logo_url ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-mono">
                  <ImageOff className="w-3 h-3" /> logo URL unreachable
                </span>
              ) : null}
              <span
                className="text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ color: HEX_RE.test(cfg.accent_hex) ? cfg.accent_hex : "#0033cc" }}
                data-testid="brand-preview-company"
              >
                {cfg.company_name || "Your Company"} · {cfg.tagline || "Tagline"}
              </span>
            </div>
            <div className="mt-3 font-display text-3xl md:text-4xl font-black tracking-tight">
              ACCESSIBILITY RISK REPORT
            </div>
            <div className="mt-3 text-[11px] text-zinc-400 font-mono">
              Prepared for <strong className="text-white">Acme Bakery, LLC</strong> · Site
              audited: https://example.com
              <br />
              Report compiled: Today · Prepared by{" "}
              <span className="text-white">{cfg.company_name || "Your Company"}</span>
            </div>
          </div>

          <div className="mt-4 border border-zinc-300 p-4">
            <div className="text-xs text-zinc-700">
              <strong>Executive Summary.</strong> Our scanner identified{" "}
              <strong>14 accessibility failures</strong> on the target site (9 of them rated HIGH
              severity)…
            </div>
            <div
              className="mt-3 text-[10px] font-mono"
              style={{
                color: HEX_RE.test(cfg.accent_hex) ? cfg.accent_hex : "#0033cc",
                borderTop: "1px solid #d4d4d4",
                paddingTop: 6,
              }}
            >
              DETAILED PITCH NARRATIVE
            </div>
          </div>

          <div
            className="mt-4 text-[10px] text-zinc-500 border-t border-zinc-300 pt-2 font-mono"
            data-testid="brand-preview-footer"
          >
            {cfg.company_name || "Your Company"} · {cfg.footer_line || "Footer"}
          </div>
        </div>
      </div>
    </div>
  );
}
